import type { components } from '@feedfm/clips-services-api';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as styles from './CatalogTable.styles';

type CatalogFiltersType =
  components['schemas']['CurationCatalogRequest']['filters'];

/**
 * Describes a single filter field that can be rendered in the filter panel.
 */
export interface FilterFieldConfig {
  /** The filter key that maps to the API request (e.g. 'titleContains', 'bpmGreaterThan') */
  key: string;
  /** The display label for this field */
  label: string;
  /** The input type */
  type: 'text' | 'number' | 'date' | 'select';
  /** For select type, the available options */
  options?: { value: string; label: string }[];
  /** Group multiple fields in the same row */
  group?: string;
  /** Flex sizing within a row */
  flex?: number;
}

interface CatalogTableFiltersProps {
  filters: CatalogFiltersType | undefined;
  onFiltersChange: (filters: CatalogFiltersType | undefined) => void;
  /** Which filter fields to render */
  filterFields: FilterFieldConfig[];
}

export const CatalogTableFilters = ({
  filters,
  onFiltersChange,
  filterFields,
}: CatalogTableFiltersProps) => {
  const [localFilters, setLocalFilters] = useState<
    CatalogFiltersType | undefined
  >(filters || {});
  const lastFiltersRef = useRef<string>('');

  // Sync filters prop changes to local state
  useEffect(() => {
    const filtersStr = JSON.stringify(filters || {});
    if (filtersStr !== lastFiltersRef.current) {
      lastFiltersRef.current = filtersStr;
      setLocalFilters(filters || {});
    }
  }, [filters]);

  const updateFilter = useCallback(
    (key: string, value: any) => {
      const newFilters = {
        ...(localFilters || {}),
        [key]: value,
      } as CatalogFiltersType;
      setLocalFilters(newFilters);
      onFiltersChange(newFilters);
    },
    [localFilters, onFiltersChange],
  );

  const removeFilter = useCallback(
    (key: string) => {
      const newFilters = { ...(localFilters || {}) } as CatalogFiltersType;
      if (newFilters) {
        delete (newFilters as any)[key];
      }
      const finalFilters =
        Object.keys(newFilters || {}).length > 0 ? newFilters : undefined;
      setLocalFilters(finalFilters);
      onFiltersChange(finalFilters);
    },
    [localFilters, onFiltersChange],
  );

  const clearAllFilters = useCallback(() => {
    setLocalFilters({});
    onFiltersChange(undefined);
  }, [onFiltersChange]);

  // Get active filters for display
  const getActiveFilters = (
    filters?: CatalogFiltersType,
  ): Record<string, any> => {
    if (!filters) return {};
    const active: Record<string, any> = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          if (value.length > 0) active[key] = value;
        } else {
          active[key] = value;
        }
      }
    });
    return active;
  };

  const activeFilters = getActiveFilters(localFilters);
  const hasActiveFilters = Object.keys(activeFilters).length > 0;

  // Friendly label mapping for active filter chips
  const getFilterLabel = (key: string): string => {
    const field = filterFields.find((f) => f.key === key);
    if (field) return field.label;
    // Fallback: convert camelCase to readable
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (s) => s.toUpperCase())
      .trim();
  };

  // Group filter fields by their group property
  const groupedFields: { group: string; fields: FilterFieldConfig[] }[] = [];
  const seenGroups = new Set<string>();

  for (const field of filterFields) {
    const groupKey = field.group || field.key;
    if (!seenGroups.has(groupKey)) {
      seenGroups.add(groupKey);
      groupedFields.push({
        group: groupKey,
        fields: filterFields.filter(
          (f) => (f.group || f.key) === groupKey,
        ),
      });
    }
  }

  const renderField = (field: FilterFieldConfig) => {
    const value = (localFilters as any)?.[field.key] ?? '';

    switch (field.type) {
      case 'select':
        return (
          <FormControl
            size="small"
            sx={styles.filterFieldFlex(field.flex)}
            key={field.key}
          >
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              label={field.label}
              onChange={(e) =>
                updateFilter(field.key, e.target.value || undefined)
              }
            >
              <MenuItem value="">All</MenuItem>
              {field.options?.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'number':
        return (
          <TextField
            key={field.key}
            label={field.label}
            type="number"
            value={value}
            onChange={(e) =>
              updateFilter(
                field.key,
                e.target.value ? parseInt(e.target.value, 10) : undefined,
              )
            }
            size="small"
            sx={styles.filterFieldFlex(field.flex)}
          />
        );
      case 'date':
        return (
          <TextField
            key={field.key}
            label={field.label}
            type="date"
            value={value}
            onChange={(e) =>
              updateFilter(field.key, e.target.value || undefined)
            }
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={styles.filterFieldFlex(field.flex)}
          />
        );
      case 'text':
      default:
        return (
          <TextField
            key={field.key}
            label={field.label}
            value={value}
            onChange={(e) =>
              updateFilter(field.key, e.target.value || undefined)
            }
            size="small"
            sx={styles.filterFieldFlex(field.flex)}
          />
        );
    }
  };

  return (
    <Box sx={styles.filtersWrapper}>
      <Accordion sx={styles.filtersAccordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">
            Filters{' '}
            {hasActiveFilters && `(${Object.keys(activeFilters).length})`}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={styles.filtersAccordionDetailsBackground}>
          <Box sx={styles.filtersAccordionDetails}>
            {groupedFields.map(({ group, fields }) => (
              <Box key={group} sx={styles.filtersRow}>
                {fields.map((field) => renderField(field))}
              </Box>
            ))}

            {/* Active Filters */}
            {hasActiveFilters && (
              <Box sx={styles.activeFiltersSection}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={styles.activeFiltersLabel}
                >
                  Active Filters:
                </Typography>
                <Box sx={styles.activeFiltersChips}>
                  {Object.entries(activeFilters).map(([key, value]) => (
                    <Chip
                      key={key}
                      label={`${getFilterLabel(key)}: ${String(value)}`}
                      onDelete={() => removeFilter(key)}
                      size="small"
                    />
                  ))}
                </Box>
                <Button
                  size="small"
                  onClick={clearAllFilters}
                  sx={styles.clearAllFiltersButton}
                >
                  Clear All
                </Button>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
